/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import debounce from 'lodash.debounce';
import resetValue from '@ion/dls/src/utils/ComponentUtils';
import {setFleetUpdateResetFlag} from '../../../store/actions/tracking';

/**
 * The `useFleetResetUpdates` hook is a custom React hook designed to reset
 * fleet update flags within a `Redux store`. It leverages react-redux for
 * state management and react's `useCallback` for performance optimization.
 * This hook encapsulates the logic required to dispatch an action that
 * resets specific fleet update states, promoting code reusability and maintainability.
 * 
 * ```typescript
 * 
 *  import {useFleetResetUpdates} from './useFleetResetUpdates';
 * 
 *  const resetUpdates = useFleetResetUpdates();
 * 
 * ```
 */
export const useFleetResetUpdates = () => {
  const dispatch = useDispatch();
  /**
   * Resets fleet update flags by invoking the resetValue utility
   * and dispatching the setFleetUpdateResetFlag action.
   */
  return useCallback(debounce((isReady = true) => {
    if (isReady) {
      resetValue({
        initialValue : false,
        resetValue   : true,
        fnCall       : (value: boolean) => {
          dispatch(setFleetUpdateResetFlag(value));
        }
      });
    }
  }, 500), []);
};

export default useFleetResetUpdates;
